/**
 * Trip session state machine — pure, no I/O.
 *
 * Drives the user through the lifecycle of a single trip:
 *   idle → connected → driving → ended
 *
 * Caller (the BT driver / UI layer) feeds it PidReading events. The state
 * machine accumulates trip metrics that the persona-inference module
 * later consumes.
 *
 * Reference:
 *   docs/superpowers/plans/2026-04-27-vibe-network-mvp.md (Chunk 7 Task 7.7)
 */

import type { PidReading } from './obd-parser';

export type TripState = 'idle' | 'connected' | 'driving' | 'ended';

export interface GeoPoint {
    lat: number;
    lng: number;
    accuracy: number;
    at: number; // unix sec
}

export interface TripSnapshot {
    state: TripState;
    started_at: number | null;     // unix sec
    ended_at: number | null;
    duration_s: number;
    distance_km: number;
    max_speed_kmh: number;
    hard_brakes: number;
    /**
     * Sum of |speed change| samples — proxy for smoothness once normalized
     * by duration. Caller divides by duration_s for a per-second value.
     */
    total_speed_jitter: number;
    /**
     * Last seen speed reading, used for jitter & distance integration.
     */
    last_speed_kmh: number | null;
    last_speed_at: number | null;
    /**
     * Count of seconds the engine was actually moving (speed > 1 km/h).
     * Differentiates a 30-min drive (mostly moving) from a 30-min idle.
     */
    moving_s: number;
    /**
     * Array of GPS breadcrumbs collected during the trip.
     */
    route: GeoPoint[];
    /** Most recent RPM reading (for live display). */
    last_rpm: number | null;
    /** Peak RPM observed during the trip. */
    max_rpm: number;
    /** Running sum of RPM samples (divide by rpm_count for average). */
    rpm_sum: number;
    /** Count of RPM samples received. */
    rpm_count: number;
    /** Most recent throttle % (for live display). */
    last_throttle_pct: number | null;
    /** Peak throttle % observed during the trip. */
    max_throttle_pct: number;
    /** Running sum of throttle % samples. */
    throttle_sum: number;
    /** Count of throttle samples received. */
    throttle_count: number;
}

export function emptySnapshot(): TripSnapshot {
    return {
        state: 'idle',
        started_at: null,
        ended_at: null,
        duration_s: 0,
        distance_km: 0,
        max_speed_kmh: 0,
        hard_brakes: 0,
        total_speed_jitter: 0,
        last_speed_kmh: null,
        last_speed_at: null,
        moving_s: 0,
        route: [],
        last_rpm: null,
        max_rpm: 0,
        rpm_sum: 0,
        rpm_count: 0,
        last_throttle_pct: null,
        max_throttle_pct: 0,
        throttle_sum: 0,
        throttle_count: 0,
    };
}

/** Threshold for counting a speed drop as a "hard brake" (km/h per second). */
export const HARD_BRAKE_KMH_PER_SEC = 12;
/** Speed at or above which the vehicle is considered "moving". */
export const MOVING_THRESHOLD_KMH = 1.5;

export type TripEvent =
    | { kind: 'connected'; at: number }
    | { kind: 'start'; at: number }
    | { kind: 'reading'; reading: PidReading }
    | { kind: 'location'; point: GeoPoint }
    | { kind: 'end'; at: number }
    | { kind: 'reset' };

/**
 * Apply an event to the snapshot. Returns the new snapshot (immutable).
 * Invalid transitions are silently ignored to keep the UI resilient.
 */
export function applyEvent(prev: TripSnapshot, event: TripEvent): TripSnapshot {
    switch (event.kind) {
        case 'connected': {
            if (prev.state === 'idle') return { ...prev, state: 'connected' };
            return prev;
        }
        case 'start': {
            if (prev.state !== 'connected' && prev.state !== 'idle') return prev;
            return {
                ...emptySnapshot(),
                state: 'driving',
                started_at: event.at,
            };
        }
        case 'reading': {
            if (prev.state !== 'driving') return prev;
            return applyReading(prev, event.reading);
        }
        case 'location': {
            if (prev.state !== 'driving') return prev;
            return { ...prev, route: [...prev.route, event.point] };
        }
        case 'end': {
            if (prev.state !== 'driving') return prev;
            const duration_s = prev.started_at ? Math.max(0, event.at - prev.started_at) : prev.duration_s;
            return { ...prev, state: 'ended', ended_at: event.at, duration_s };
        }
        case 'reset': {
            return emptySnapshot();
        }
    }
}

function applyReading(snap: TripSnapshot, r: PidReading): TripSnapshot {
    if (r.pid === 'rpm') {
        return {
            ...snap,
            last_rpm: r.value,
            max_rpm: Math.max(snap.max_rpm, r.value),
            rpm_sum: snap.rpm_sum + r.value,
            rpm_count: snap.rpm_count + 1,
        };
    }

    if (r.pid === 'throttle') {
        return {
            ...snap,
            last_throttle_pct: r.value,
            max_throttle_pct: Math.max(snap.max_throttle_pct, r.value),
            throttle_sum: snap.throttle_sum + r.value,
            throttle_count: snap.throttle_count + 1,
        };
    }

    // Speed reading — integrate distance, jitter, hard brakes, moving time.
    const speed = r.value;
    const next: TripSnapshot = {
        ...snap,
        max_speed_kmh: Math.max(snap.max_speed_kmh, speed),
    };
    next.duration_s = snap.started_at ? Math.max(0, r.at - snap.started_at) : snap.duration_s;

    if (snap.last_speed_kmh !== null && snap.last_speed_at !== null) {
        const dt = Math.max(0.05, r.at - snap.last_speed_at);   // seconds, clamped to avoid /0
        // Trapezoidal integration of speed (km/h * sec → km).
        const avg = (snap.last_speed_kmh + speed) / 2;
        next.distance_km = snap.distance_km + (avg * dt) / 3600;

        // Jitter accumulates regardless of sign.
        const dv = Math.abs(speed - snap.last_speed_kmh);
        next.total_speed_jitter = snap.total_speed_jitter + dv;

        // Hard brake = speed dropped fast enough.
        const drop = (snap.last_speed_kmh - speed) / dt;
        if (drop >= HARD_BRAKE_KMH_PER_SEC) {
            next.hard_brakes = snap.hard_brakes + 1;
        }

        if (speed >= MOVING_THRESHOLD_KMH || snap.last_speed_kmh >= MOVING_THRESHOLD_KMH) {
            next.moving_s = snap.moving_s + dt;
        }
    }

    next.last_speed_kmh = speed;
    next.last_speed_at = r.at;
    return next;
}

/**
 * Final summary derived from a trip snapshot. Only meaningful in `ended`
 * state; caller should guard.
 */
export interface TripSummary {
    duration_s: number;
    distance_km: number;
    max_speed_kmh: number;
    hard_brakes: number;
    moving_s: number;
    /** smoothness ∈ [0, 1]; 1 = perfectly smooth, 0 = very jittery */
    smoothness: number;
    route: GeoPoint[];
    /** Peak engine RPM recorded during the trip. 0 if no RPM data. */
    max_rpm: number;
    /** Average engine RPM across all samples. 0 if no RPM data. */
    avg_rpm: number;
    /** Peak throttle position % recorded during the trip. 0 if no data. */
    max_throttle_pct: number;
    /** Average throttle position % across all samples. 0 if no data. */
    avg_throttle_pct: number;
}

/**
 * Smoothness model — heuristic for v1, replaces a more sophisticated
 * driver-behavior model later. Penalize jitter per moving-second + each
 * hard brake. Never < 0 or > 1.
 *
 * Defaults tuned so that:
 *   - 30-min smooth drive (5 km/h jitter average): smoothness ≈ 0.85
 *   - 30-min city drive (12 km/h jitter average, 2 hard brakes): ≈ 0.5
 *   - Aggressive driving (jitter 20+, 5+ brakes): ≈ 0.2
 */
export function computeSmoothness(s: { total_speed_jitter: number; moving_s: number; hard_brakes: number }): number {
    if (s.moving_s < 5) return 0.5;     // not enough data — neutral
    const jitterPerSec = s.total_speed_jitter / s.moving_s;
    const jitterPenalty = Math.min(0.6, jitterPerSec * 0.05);       // at jitter=12, -0.6
    const brakePenalty = Math.min(0.4, s.hard_brakes * 0.08);       // at 5 brakes, -0.4
    const score = 1 - jitterPenalty - brakePenalty;
    if (score < 0) return 0;
    if (score > 1) return 1;
    return score;
}

export function snapshotToSummary(snap: TripSnapshot): TripSummary {
    return {
        duration_s: snap.duration_s,
        distance_km: snap.distance_km,
        max_speed_kmh: snap.max_speed_kmh,
        hard_brakes: snap.hard_brakes,
        moving_s: snap.moving_s,
        smoothness: computeSmoothness(snap),
        route: snap.route,
        max_rpm: snap.max_rpm,
        avg_rpm: snap.rpm_count > 0 ? snap.rpm_sum / snap.rpm_count : 0,
        max_throttle_pct: snap.max_throttle_pct,
        avg_throttle_pct: snap.throttle_count > 0 ? snap.throttle_sum / snap.throttle_count : 0,
    };
}
